import { useState, useEffect } from "react";
import { Package, Plus, Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryItemSelector({ onSelect, buttonVariant = "outline" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      loadItems();
    }
  }, [open]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/items`);
      setItems(response.data);
    } catch (error) {
      console.error("Failed to load inventory items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    onSelect({
      inventory_item_id: item.id,
      description: item.name,
      unit_price: item.selling_price || 0,
      quantity: 1,
      amount: item.selling_price || 0,
      unit: item.unit,
      cost_price: item.cost_price
    });
    setOpen(false);
    setSearchQuery("");
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      (item.sku && item.sku.toLowerCase().includes(query))
    );
  });

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={buttonVariant}
        onClick={() => setOpen(true)}
        data-testid="inventory-selector-btn"
      >
        <Package className="w-4 h-4 mr-1" />
        From Inventory
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="inventory-selector-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Select Inventory Item
            </DialogTitle>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="inventory-search-input"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                {items.length === 0 ? (
                  <div>
                    <p>No inventory items yet.</p>
                    <Button
                      variant="link"
                      className="mt-2"
                      onClick={() => window.open("/dashboard/inventory", "_blank")}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add items to inventory
                    </Button>
                  </div>
                ) : (
                  "No items match your search"
                )}
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {filteredItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className="w-full text-left p-3 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
                    data-testid={`inventory-item-${item.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.classification && (
                            <Badge variant="outline" className="text-xs">
                              {item.classification.name}
                            </Badge>
                          )}
                          {item.sku && (
                            <span className="text-xs text-gray-500">SKU: {item.sku}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {item.selling_price && (
                          <p className="font-semibold text-primary">
                            ${item.selling_price.toFixed(2)}
                          </p>
                        )}
                        {item.track_quantity && (
                          <p className="text-xs text-gray-500">
                            {item.current_quantity} {item.unit} in stock
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
